import React, { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MultiStepPropertyForm } from "./multi-step-property-form";
import { PropertyDialog } from "./property-dialog";
import { Settings, Plus } from "lucide-react";
import type { Id } from "../../convex/_generated/dataModel";

interface PropertyFormWrapperProps {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit" | "view";
  propertyId?: Id<"base_properties">;
  property?: any;
  draftId?: Id<"property_drafts">;
  draftData?: {
    base: any;
    types: {
      selected: string[];
      data: Record<string, any>;
    };
    facilities: any;
    contact: any;
    step: number;
  };
}

type FormType = "simple" | "advanced";

export function PropertyFormWrapper({
  open,
  onClose,
  mode,
  propertyId,
  property,
  draftId,
  draftData,
}: PropertyFormWrapperProps) {
  const [formType, setFormType] = useState<FormType | null>(
    mode === "create" && !draftData ? null : "advanced",
  );

  const handleFormTypeSelect = (type: FormType) => {
    setFormType(type);
  };

  // render form type selection for create mode
  if (open && mode === "create" && formType === null) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center space-y-6 p-6">
            <div className="text-center space-y-2">
              <h2 className="text-xl font-semibold">Choose Form Type</h2>
              <p className="text-sm text-muted-foreground">
                Select how you'd like to create your property
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 w-full">
              <Button
                variant="outline"
                className="h-auto p-6 flex flex-col items-center space-y-3 hover:bg-primary/5 hover:border-primary"
                onClick={() => handleFormTypeSelect("advanced")}
              >
                <Settings className="h-8 w-8 text-primary" />
                <div className="text-center w-full">
                  <div className="font-medium">Multi-Step Form</div>
                  <div className="text-xs text-muted-foreground whitespace-normal">
                    Comprehensive form with property types, facilities, and
                    validation
                  </div>
                </div>
              </Button>
            </div>

            <Button variant="ghost" onClick={onClose} className="w-full">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // render selected form type
  if (formType === "advanced" && mode !== "view") {
    return (
      <MultiStepPropertyForm
        open={open}
        onClose={() => {
          setFormType(null); // reset for next time
          onClose();
        }}
        mode={mode}
        propertyId={propertyId}
        draftId={draftId}
        draftData={draftData}
      />
    );
  }

  // use PropertyDialog for simple form or view mode
  if (formType === "simple" || mode === "view") {
    return (
      <PropertyDialog
        open={open}
        onClose={() => {
          setFormType(null); // reset for next time
          onClose();
        }}
        mode={mode}
        property={property}
      />
    );
  }

  return null;
}
